import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiStar, FiAward, FiHeart, FiClock, FiCalendar
} from 'react-icons/fi';
import { GiBed, GiSwordman, GiCrossedSwords } from 'react-icons/gi';
import PlayerAvatar from './PlayerAvatar';
import StatCard from './StatCard';
import BedwarsStats from './BedwarsStats';
import SkywarsStats from './SkywarsStats';
import DuelsStats from './DuelsStats';
import './PlayerCard.css';

/**
 * Main player card component displaying all player information
 */
const PlayerCard = ({ player }) => {
  const [activeTab, setActiveTab] = useState('overview');

  // Format large numbers with commas
  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  // Format timestamp to readable date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Time since last login
  const getTimeSince = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = Date.now();
    const diff = now - timestamp;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 30) return `${days} days ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return `${Math.floor(days / 365)} years ago`;
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <FiStar /> },
    { id: 'bedwars', label: 'Bedwars', icon: <GiBed /> },
    { id: 'skywars', label: 'Skywars', icon: <GiSwordman /> },
    { id: 'duels', label: 'Duels', icon: <GiCrossedSwords /> }
  ];

  return (
    <motion.div
      className="player-card"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Player Header */}
      <div className="player-header">
        <PlayerAvatar uuid={player.uuid} username={player.username} />

        <div className="player-info">
          <div className="player-name-row">
            <h2 className="player-name">{player.username}</h2>
            <span
              className="player-rank"
              style={{ backgroundColor: player.rankColor }}
            >
              {player.rank}
            </span>
          </div>

          <div className="player-meta">
            <div className="meta-item">
              <FiStar />
              <span>Level {player.networkLevel}</span>
            </div>
            <div className="meta-item">
              <FiHeart />
              <span>{formatNumber(player.karma)} Karma</span>
            </div>
            <div className="meta-item">
              <FiAward />
              <span>{formatNumber(player.achievementPoints)} AP</span>
            </div>
          </div>

          <div className="player-dates">
            <div className="date-item">
              <FiCalendar />
              <span>First Login: {formatDate(player.firstLogin)}</span>
            </div>
            <div className="date-item">
              <FiClock />
              <span>Last Seen: {getTimeSince(player.lastLogin)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        {tabs.map((tab) => (
          <motion.button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </motion.button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <motion.div
            className="overview-grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <StatCard
              title="Bedwars Level"
              value={player.bedwars?.level || 0}
              icon={<GiBed />}
              color="#e74c3c"
              subtitle={`FKDR: ${player.bedwars?.fkdr || '0'}`}
            />
            <StatCard
              title="Skywars Level"
              value={player.skywars?.level || 0}
              icon={<GiSwordman />}
              color="#9b59b6"
              subtitle={`KDR: ${player.skywars?.kdr || '0'}`}
            />
            <StatCard
              title="Duels Wins"
              value={formatNumber(player.duels?.wins)}
              icon={<GiCrossedSwords />}
              color="#3498db"
              subtitle={`WLR: ${player.duels?.wlr || '0'}`}
            />
            <StatCard
              title="Network Level"
              value={player.networkLevel}
              icon={<FiStar />}
              color="#f39c12"
              subtitle={`${formatNumber(player.karma)} Karma`}
            />
          </motion.div>
        )}

        {activeTab === 'bedwars' && (
          <BedwarsStats stats={player.bedwars} />
        )}

        {activeTab === 'skywars' && (
          <SkywarsStats stats={player.skywars} />
        )}

        {activeTab === 'duels' && (
          <DuelsStats stats={player.duels} />
        )}
      </div>
    </motion.div>
  );
};

export default PlayerCard;
